import { api } from "./baseApi";
import {
  ApiResponse,
  Expense,
  ExpenseCategory,
  Budget,
  ExpenseStats,
  StoreExpenseSummary,
  OutletExpenseDetail,
  StoreCashSummary,
  StoreCashFlow,
  MultiOutletAnalyticsFilters
} from "@/types";

export interface ExpenseQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  categoryId?: string;
  subcategoryId?: string;
  status?: "PENDING" | "APPROVED" | "REJECTED" | "PAID";
  paymentMethod?: "CASH" | "CARD" | "TRANSFER" | "CHECK";
  startDate?: string;
  endDate?: string;
  sortBy?: "date" | "amount" | "title" | "vendor" | "createdAt";
  sortOrder?: "asc" | "desc";
  userId?: string;
  isRecurring?: boolean;
  tags?: string[];
  outletId?: string; // Filter by outlet
  storeId?: string; // Filter by store (explicit context)
}

export interface CreateExpenseData {
  title: string;
  type: "Salary" | "Utilities" | "Supplies" | "Rent" | "Maintenance" | "Other";
  categoryId?: string;
  amount: number;
  description?: string;
  frequency?: "OneTime" | "Daily" | "Weekly" | "Monthly" | "Yearly";
  paymentDate: string;
  paymentMethod?: "CASH" | "CARD" | "TRANSFER" | "CHECK";
  cashSource?: "STORE_CASH" | "CASH_DRAWER";
  receiptNumber?: string;
  vendor?: string;
  tags?: string[];
  notes?: string;
  isRecurring?: boolean;
  recurringFrequency?: "MONTHLY" | "QUARTERLY" | "YEARLY" | "DAILY" | "WEEKLY" | "BIWEEKLY" | "CUSTOM";
  recurringEndDate?: string;
  status?: "PENDING" | "APPROVED" | "REJECTED" | "PAID";
  outletId?: string; // Assign to outlet
}

export interface UpdateExpenseData {
  title?: string;
  description?: string;
  amount?: number;
  categoryId?: string;
  subcategoryId?: string;
  paymentDate?: string;
  receiptNumber?: string;
  vendor?: string;
  paymentMethod?: "CASH" | "CARD" | "TRANSFER" | "CHECK";
  cashSource?: "STORE_CASH" | "CASH_DRAWER";
  isRecurring?: boolean;
  recurringFrequency?: "MONTHLY" | "QUARTERLY" | "YEARLY" | "DAILY" | "WEEKLY" | "BIWEEKLY" | "CUSTOM";
  recurringEndDate?: string;
  status?: "PENDING" | "APPROVED" | "REJECTED" | "PAID";
  tags?: string[];
  notes?: string;
}

export interface CreateExpenseCategoryData {
  name: string;
  description?: string;
  budgetLimit?: number;
  color?: string;
  icon?: string;
  storeId: string; // Required for explicit context
}

export interface UpdateExpenseCategoryData {
  name?: string;
  description?: string;
  budgetLimit?: number;
  isActive?: boolean;
  color?: string;
  icon?: string;
  storeId?: string; // Optional for validation
}

export interface CreateExpenseSubcategoryData {
  name: string;
  description?: string;
  parentId: string; // This is the parent category ID (subcategory creation)
  budgetLimit?: number;
  color?: string;
  icon?: string;
  storeId: string; // Required for explicit context
}

export interface UpdateExpenseSubcategoryData {
  name?: string;
  description?: string;
  budgetLimit?: number;
  isActive?: boolean;
  color?: string;
  icon?: string;
}

export interface CreateBudgetData {
  name: string;
  categoryId?: string;
  subcategoryId?: string;
  amount: number;
  period: "MONTHLY" | "QUARTERLY" | "YEARLY";
  startDate: string;
  endDate: string;
  alertThreshold: number;
}

export interface UpdateBudgetData {
  name?: string;
  categoryId?: string;
  subcategoryId?: string;
  amount?: number;
  period?: "MONTHLY" | "QUARTERLY" | "YEARLY";
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  alertThreshold?: number;
}

export interface BulkExpenseActionData {
  expenseIds: string[];
  action: "approve" | "reject" | "markPaid" | "delete" | "updateCategory" | "updateStatus";
  data?: {
    categoryId?: string;
    subcategoryId?: string;
    status?: "PENDING" | "APPROVED" | "REJECTED" | "PAID";
  };
}

export interface ExportExpenseParams {
  format: "csv" | "excel" | "pdf";
  fields: string[];
  expenseIds?: string[];
  categoryId?: string;
  subcategoryId?: string;
  status?: string[];
  paymentMethod?: string[];
  startDate?: string;
  endDate?: string;
  outletId?: string;
  storeId?: string;
}

export interface ExpensePaginatedResponse {
  expenses: Expense[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const expenseApi = api.injectEndpoints({
  endpoints: builder => ({
    // Expense CRUD operations
    getExpenses: builder.query<ApiResponse<ExpensePaginatedResponse>, ExpenseQueryParams>({
      query: params => ({
        url: "/expenses",
        params,
      }),
      providesTags: ["Expense"],
    }),

    getExpense: builder.query<ApiResponse<Expense>, string>({
      query: id => `/expenses/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Expense", id }],
    }),

    createExpense: builder.mutation<ApiResponse<Expense>, CreateExpenseData>({
      query: data => ({
        url: "/expenses",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Expense", "ExpenseStats", "CashDrawer", "StoreCash", "Report"],
    }),

    updateExpense: builder.mutation<ApiResponse<Expense>, { id: string; data: UpdateExpenseData }>({
      query: ({ id, data }) => ({
        url: `/expenses/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Expense", id },
        "Expense",
        "ExpenseStats",
        "Report",
      ],
    }),

    deleteExpense: builder.mutation<ApiResponse<void>, string>({
      query: id => ({
        url: `/expenses/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Expense", id },
        "Expense",
        "ExpenseStats",
        "Report",
      ],
    }),

    // Expense Categories
    getExpenseCategories: builder.query<ApiResponse<ExpenseCategory[]>, string | undefined>({
      query: (storeId) => ({
        url: "/expenses/categories",
        params: { storeId },
      }),
      providesTags: ["ExpenseCategory"],
    }),

    getExpenseCategory: builder.query<ApiResponse<ExpenseCategory>, string>({
      query: id => `/expenses/categories/${id}`,
      providesTags: (_result, _error, id) => [{ type: "ExpenseCategory", id }],
    }),

    createExpenseCategory: builder.mutation<
      ApiResponse<ExpenseCategory>,
      CreateExpenseCategoryData
    >({
      query: data => ({
        url: "/expenses/categories",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ExpenseCategory"],
    }),

    updateExpenseCategory: builder.mutation<
      ApiResponse<ExpenseCategory>,
      { id: string; data: UpdateExpenseCategoryData }
    >({
      query: ({ id, data }) => ({
        url: `/expenses/categories/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ExpenseCategory", id },
        "ExpenseCategory",
        "Report",
      ],
    }),

    deleteExpenseCategory: builder.mutation<ApiResponse<void>, { id: string; storeId?: string }>({
      query: ({ id, storeId }) => ({
        url: `/expenses/categories/${id}`,
        method: "DELETE",
        params: { storeId },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ExpenseCategory", id },
        "ExpenseCategory",
        "Report",
      ],
    }),

    // Expense Subcategories - using hierarchical categories
    getExpenseSubcategories: builder.query<ApiResponse<ExpenseCategory[]>, string>({
      query: categoryId => `/expenses/categories?parentId=${categoryId}`,
      providesTags: ["ExpenseCategory"],
    }),

    createExpenseSubcategory: builder.mutation<
      ApiResponse<ExpenseCategory>,
      CreateExpenseCategoryData & { parentId: string }
    >({
      query: data => ({
        url: "/expenses/categories",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ExpenseCategory"],
    }),

    updateExpenseSubcategory: builder.mutation<
      ApiResponse<ExpenseCategory>,
      { id: string; data: UpdateExpenseCategoryData }
    >({
      query: ({ id, data }) => ({
        url: `/expenses/categories/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ExpenseCategory", id },
        "ExpenseCategory",
        "Report",
      ],
    }),

    deleteExpenseSubcategory: builder.mutation<ApiResponse<void>, string>({
      query: id => ({
        url: `/expenses/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "ExpenseCategory", id },
        "ExpenseCategory",
        "Report",
      ],
    }),

    // Budget Management
    getBudgets: builder.query<ApiResponse<Budget[]>, void>({
      query: () => "/expenses/budgets",
      providesTags: ["Budget"],
    }),

    getBudget: builder.query<ApiResponse<Budget>, string>({
      query: id => `/expenses/budgets/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Budget", id }],
    }),

    createBudget: builder.mutation<ApiResponse<Budget>, CreateBudgetData>({
      query: data => ({
        url: "/expenses/budgets",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Budget"],
    }),

    updateBudget: builder.mutation<ApiResponse<Budget>, { id: string; data: UpdateBudgetData }>({
      query: ({ id, data }) => ({
        url: `/expenses/budgets/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Budget", id }, "Budget"],
    }),

    deleteBudget: builder.mutation<ApiResponse<void>, string>({
      query: id => ({
        url: `/expenses/budgets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "Budget", id }, "Budget"],
    }),

    // Statistics and Analytics
    getExpenseStats: builder.query<
      ApiResponse<ExpenseStats>,
      { startDate?: string; endDate?: string; outletId?: string; storeId?: string }
    >({
      query: params => ({
        url: "/expenses/stats",
        params,
      }),
      providesTags: ["ExpenseStats"],
    }),

    // Upload expense receipt - following item image upload pattern
    uploadExpenseReceipt: builder.mutation<
      ApiResponse<{
        id: string;
        filename: string;
        path: string;
        size: number;
        mimetype: string;
      }>,
      { expenseId: string; receipt: File; storeId: string }
    >({
      query: ({ expenseId, receipt, storeId }) => {
        const formData = new FormData();
        formData.append("receipt", receipt);
        formData.append("storeId", storeId);
        return {
          url: `/expenses/${expenseId}/upload-receipt`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_, __, { expenseId }) => [{ type: "Expense", id: expenseId }, "Expense"],
    }),

    // Note: Receipt fetching is handled by useExpenseReceipt hook to avoid storing Blobs in Redux state

    // Bulk Operations
    bulkExpenseAction: builder.mutation<
      ApiResponse<{ processed: number; failed: number }>,
      BulkExpenseActionData
    >({
      query: data => ({
        url: "/expenses/bulk-action",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Expense", "ExpenseStats", "CashDrawer", "StoreCash", "Report"],
    }),

    // Export
    exportExpenses: builder.mutation<Blob, ExportExpenseParams>({
      query: params => ({
        url: "/expenses/export",
        method: "POST",
        body: params,
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),

    // Approve/Reject expenses
    approveExpense: builder.mutation<ApiResponse<Expense>, string>({
      query: id => ({
        url: `/expenses/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "Expense", id },
        "Expense",
        "ExpenseStats",
        "Report",
      ],
    }),

    rejectExpense: builder.mutation<ApiResponse<Expense>, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/expenses/${id}/reject`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Expense", id },
        "Expense",
        "ExpenseStats",
        "Report",
      ],
    }),

    // Multi-Outlet Analytics
    getStoreExpenseSummary: builder.query<
      ApiResponse<StoreExpenseSummary>,
      { storeId: string } & MultiOutletAnalyticsFilters
    >({
      query: ({ storeId, ...params }) => ({
        url: `/stores/${storeId}/expense-summary`,
        params,
      }),
      providesTags: ["ExpenseStats"],
    }),

    getOutletExpenseSummary: builder.query<
      ApiResponse<OutletExpenseDetail>,
      { outletId: string } & MultiOutletAnalyticsFilters
    >({
      query: ({ outletId, ...params }) => ({
        url: `/outlets/${outletId}/expense-summary`,
        params,
      }),
      providesTags: ["ExpenseStats"],
    }),

    getStoreCashSummary: builder.query<ApiResponse<StoreCashSummary>, string>({
      query: storeId => `/stores/${storeId}/cash-summary`,
      providesTags: ["StoreCash", "CashDrawer"],
    }),

    getStoreCashFlow: builder.query<
      ApiResponse<StoreCashFlow>,
      { storeId: string } & MultiOutletAnalyticsFilters
    >({
      query: ({ storeId, ...params }) => ({
        url: `/stores/${storeId}/cash-flow`,
        params,
      }),
      providesTags: ["ExpenseStats", "StoreCash", "CashDrawer"],
    }),
  }),
});

export const {
  useGetExpensesQuery,
  useGetExpenseQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
  useGetExpenseCategoriesQuery,
  useGetExpenseCategoryQuery,
  useCreateExpenseCategoryMutation,
  useUpdateExpenseCategoryMutation,
  useDeleteExpenseCategoryMutation,
  useGetExpenseSubcategoriesQuery,
  useCreateExpenseSubcategoryMutation,
  useUpdateExpenseSubcategoryMutation,
  useDeleteExpenseSubcategoryMutation,
  useGetBudgetsQuery,
  useGetBudgetQuery,
  useCreateBudgetMutation,
  useUpdateBudgetMutation,
  useDeleteBudgetMutation,
  useGetExpenseStatsQuery,
  useUploadExpenseReceiptMutation,
  useBulkExpenseActionMutation,
  useExportExpensesMutation,
  useApproveExpenseMutation,
  useRejectExpenseMutation,
  useGetStoreExpenseSummaryQuery,
  useGetOutletExpenseSummaryQuery,
  useGetStoreCashSummaryQuery,
  useGetStoreCashFlowQuery,
} = expenseApi;
